import {
    formatListings,
    formatListingsV2,
    saveSearchAndAlertUrlFixer
} from '@zoopla/listing-search-utils';
import {
    ServerData,
    ServerDataV2,
    SearchContextType,
    mapDataToActions,
    mapDataToActionsV2,
    searchReducer
} from '@zoopla/listing-search-context';
import shuffle from 'lodash.shuffle';
import { internalPromoIds } from '@zoopla/listing-search/featured/properties';
import { Listing } from '@zoopla/listing-search-queries';

type PromoIds = (typeof internalPromoIds)[number];

function getFeaturedListingsToDisplay<T>(
    properties: (T | PromoIds)[]
): (T | PromoIds)[] {
    const shuffledPromos = shuffle(internalPromoIds);
    const items = properties.concat(shuffledPromos);

    const maxItems = 3;
    const minProperties = 1;
    return properties.length >= minProperties ? items.slice(0, maxItems) : [];
}

export const getServerRenderedData = (
    serverSideData: ServerData,
    initialState: SearchContextType
) => {
    const {
        seoAccordions,
        searchResults: {
            listings,
            pagination: {
                totalResults,
                pageNumber,
                totalResultsWasLimited,
                pageNumberMax
            },
            links,
            polyenc,
            polygon,
            seoBlurb: { transactionType }
        }
    } = serverSideData;
    const { regular, extended, featured } = listings;

    const searchContextData = mapDataToActions(serverSideData).reduce(
        searchReducer,
        {
            ...initialState,
            totalResults: totalResults ?? null,
            pageNumber: pageNumber ?? null,
            pageNumberMax: pageNumberMax ?? null,
            totalResultsWasLimited: totalResultsWasLimited ?? false,
            polyenc: polyenc ?? null,
            locationByIdentifier: seoAccordions?.[0]?.geo ?? null // TODO: use a genuine data key
        }
    );

    const formattedLinks = {
        createAlert: saveSearchAndAlertUrlFixer(links?.createAlert),
        saveSearch: saveSearchAndAlertUrlFixer(links?.saveSearch)
    };

    return {
        regularListingsFormatted: formatListings<Listing>(regular),
        extendedListingsFormatted: formatListings<Listing>(extended ?? []),
        featuredProperties: getFeaturedListingsToDisplay(featured ?? []),
        searchContextData,
        polygon,
        transactionType,
        links: formattedLinks
    };
};

export const getServerRenderedDataV2 = (
    serverSideData: ServerDataV2,
    initialState: SearchContextType
) => {
    const {
        seoAccordions,
        searchResultsV2: {
            results,
            pagination: {
                totalResults,
                pageNumber,
                totalResultsWasLimited,
                pageNumberMax
            },
            links,
            polyenc,
            polygon,
            seoBlurb: { transactionType }
        }
    } = serverSideData;
    const { main, extended } = results;

    const searchContextData = mapDataToActionsV2(serverSideData).reduce(
        searchReducer,
        {
            ...initialState,
            totalResults: totalResults ?? null,
            pageNumber: pageNumber ?? null,
            pageNumberMax: pageNumberMax ?? null,
            totalResultsWasLimited: totalResultsWasLimited ?? false,
            polyenc: polyenc ?? null,
            locationByIdentifier: seoAccordions?.[0]?.geo ?? null // TODO: use a genuine data key
        }
    );

    const formattedLinks = {
        createAlert: saveSearchAndAlertUrlFixer(links?.createAlert),
        saveSearch: saveSearchAndAlertUrlFixer(links?.saveSearch)
    };

    return {
        regularListingsFormatted: formatListingsV2(main),
        extendedListingsFormatted: formatListings(extended),
        featuredProperties: getFeaturedListingsToDisplay([]),
        searchContextData,
        polygon,
        transactionType,
        links: formattedLinks
    };
};
